import React, { useEffect, useState } from "react";
import api, { downloadAuthenticatedPdf } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Copy, Send, MessageCircle, FileText, ExternalLink, Trash2 } from "lucide-react";

const STATUS_LABEL = {
  not_generated: { label: "Não gerado", cls: "bg-slate-100 text-slate-600" },
  awaiting_release: { label: "Aguardando liberação", cls: "bg-amber-100 text-amber-800" },
  scheduled: { label: "Programado", cls: "bg-slate-100 text-slate-600" },
  link_sent: { label: "Link enviado", cls: "bg-sky-100 text-sky-700" },
  viewed: { label: "Visualizado", cls: "bg-sky-100 text-sky-700" },
  in_progress: { label: "Em preenchimento", cls: "bg-amber-100 text-amber-800" },
  completed: { label: "Concluído", cls: "bg-emerald-100 text-emerald-700" },
  completed_with_observation: { label: "Concluído (c/ observação)", cls: "bg-amber-100 text-amber-800" },
  completed_with_incident: { label: "Concluído (c/ ocorrência)", cls: "bg-red-100 text-red-700" },
  awaiting_review: { label: "Aguardando conferência", cls: "bg-amber-100 text-amber-800" },
  reviewed: { label: "Conferido", cls: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "Cancelado", cls: "bg-slate-100 text-slate-500" },
};

function Badge({ status }) {
  const s = STATUS_LABEL[status] || { label: status, cls: "bg-slate-100 text-slate-700" };
  return <span className={`text-[11px] px-2 py-0.5 rounded ${s.cls}`}>{s.label}</span>;
}

export default function AdminChecklists() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  async function load() {
    setLoading(true);
    try { const r = await api.get("/admin/checklists"); setItems(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao carregar"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function issueLink(id) {
    try {
      const r = await api.post(`/admin/checklists/${id}/issue-link`);
      await navigator.clipboard.writeText(r.data.url);
      toast.success("Link copiado. Expira em " + new Date(r.data.expires_at).toLocaleDateString("pt-BR"));
      load();
      return r.data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao gerar link"); }
  }
  async function whatsapp(id) {
    const data = await issueLink(id);
    if (data?.whatsapp_url) window.open(data.whatsapp_url, "_blank");
    else toast.error("Hóspede sem telefone cadastrado");
  }
  async function email(id) {
    try { await api.post(`/admin/checklists/${id}/send-email`); toast.success("E-mail enviado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao enviar"); }
  }

  const visible = filter === "all" ? items : items.filter((x) => x.kind === filter);

  return (
    <div className="max-w-7xl" data-testid="admin-checklists-page">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <span className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Operações</span>
          <h1 className="text-3xl font-semibold text-[#0AA89C] mt-1">Check-in e Check-out</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Checklists digitais gerados automaticamente ao aceite do contrato. Link de check-out
            enviado automaticamente 24h antes da saída. Aceite forense (Lei 14.063/2020).
          </p>
        </div>
        <div className="flex gap-2">
          {[["all", "Todos"], ["checkin", "Check-in"], ["checkout", "Check-out"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`text-xs px-3 py-1.5 rounded ${filter === k ? "bg-[#0AA89C] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div className="text-sm text-slate-500">Carregando…</div> :
        visible.length === 0 ? (
          <div className="border border-dashed border-slate-300 rounded-md p-12 text-center">
            <FileText className="w-8 h-8 text-slate-400 mx-auto" />
            <p className="text-sm text-slate-500 mt-3">Nenhum checklist gerado ainda.<br/>Serão criados automaticamente quando um contrato for aceito.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Tipo</th>
                  <th className="px-4 py-3 text-left">Hóspede</th>
                  <th className="px-4 py-3 text-left">Casa</th>
                  <th className="px-4 py-3 text-left">Reserva</th>
                  <th className="px-4 py-3 text-left">Datas</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100" data-testid={`checklist-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${c.kind === "checkin" ? "bg-emerald-50 text-emerald-700" : "bg-sky-50 text-sky-700"}`}>
                        {c.kind === "checkin" ? "Check-in" : "Check-out"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{c.reservation?.guest?.name || "—"}</td>
                    <td className="px-4 py-3 text-slate-700">{c.house?.name || "—"}</td>
                    <td className="px-4 py-3 text-slate-700">{c.reservation?.code || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{c.reservation?.check_in} → {c.reservation?.check_out}</td>
                    <td className="px-4 py-3"><Badge status={c.status} /></td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <Button size="sm" variant="outline" onClick={() => issueLink(c.id)} data-testid={`issue-link-${c.id}`}><Copy className="w-3 h-3 mr-1" />Copiar link</Button>
                      <Button size="sm" variant="outline" onClick={() => whatsapp(c.id)} data-testid={`whatsapp-${c.id}`}><MessageCircle className="w-3 h-3 mr-1" />WhatsApp</Button>
                      <Button size="sm" variant="outline" onClick={() => email(c.id)} data-testid={`email-${c.id}`}><Send className="w-3 h-3 mr-1" />E-mail</Button>
                      {c.pdf_path && (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await downloadAuthenticatedPdf(
                                `/admin/checklists/${c.id}/pdf`,
                                "inline",
                                `checklist-${c.kind}-${(c.reservation?.code || c.id.slice(0, 8))}.pdf`
                              );
                            } catch (e) { toast.error(e.message || "Erro ao abrir PDF"); }
                          }}
                          className="inline-flex items-center text-xs text-[#0AA89C] hover:text-[#066B63] px-2"
                          data-testid={`cl-view-pdf-${c.id}`}
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />PDF
                        </button>
                      )}
                      <Button
                        size="sm" variant="ghost"
                        className="text-red-600 hover:bg-red-50"
                        onClick={async () => {
                          if (!window.confirm("Mover este checklist para a Lixeira?")) return;
                          try {
                            await api.delete(`/admin/checklists/${c.id}`);
                            toast.success("Movido para Lixeira");
                            load();
                          } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
                        }}
                        data-testid={`delete-checklist-${c.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
